/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { AfterViewInit, Component, ElementRef, forwardRef, NgZone, ViewChild } from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { defaultKeymap, indentWithTab } from "@codemirror/commands";
import { json } from "@codemirror/lang-json";
import { indentOnInput } from "@codemirror/language";
import { EditorView, highlightActiveLine, keymap, lineNumbers, ViewUpdate } from "@codemirror/view";

@Component({
  selector: "app-json-editor",
  standalone: true,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => JsonEditorComponent),
      multi: true,
    },
  ],
  template: `<div #editor class="h-min-[300px]"></div>`,
})
export class JsonEditorComponent implements AfterViewInit, ControlValueAccessor {
  @ViewChild("editor", { static: true }) editorRef!: ElementRef;
  private editor!: EditorView;
  private onChange: (v: string) => void = () => {};
  private onTouched: () => void = () => {};
  private _value = "";

  constructor(private ngZone: NgZone) {}

  writeValue(value: string): void {
    this._value = value || "";
    if (this.editor) {
      this.editor.dispatch({
        changes: {
          from: 0,
          to: this.editor.state.doc.length,
          insert: this._value,
        },
      });
    }
  }
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  ngAfterViewInit(): void {
    const updateListener = EditorView.updateListener.of((update: ViewUpdate) => {
      if (update.docChanged) {
        const text = update.state.doc.toString();
        this._value = text;
        this.ngZone.run(() => this.onChange(text));
      }
    });

    const blurHandler = EditorView.domEventHandlers({
      blur: (_, view) => {
        this.ngZone.run(() => {
          this.onTouched();
          try {
            const current = view.state.doc.toString();
            const pretty = JSON.stringify(JSON.parse(current), null, 2);
            if (pretty !== current) {
              view.dispatch({
                changes: { from: 0, to: current.length, insert: pretty },
              });
              this.onChange(pretty);
            }
          } catch {
            // invalid JSON – ignore
          }
        });
        return false;
      },
    });

    this.editor = new EditorView({
      parent: this.editorRef.nativeElement,
      doc: this._value,
      extensions: [
        keymap.of([indentWithTab, ...defaultKeymap]),
        lineNumbers(),
        highlightActiveLine(),
        indentOnInput(),
        json(),
        updateListener,
        blurHandler,
      ],
    });
  }
}
